// Document AI Summary — API calls (transaction-service, via the gateway).
// Self-contained: builds its own URLs from API_BASE_URL so config/api.js is
// not modified.
import { onAuthStateChanged } from 'firebase/auth';
import { API_BASE_URL, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';

const base = (transactionId) => `${API_BASE_URL}/api/v1/transactions/${transactionId}/doc-ai-summaries`;

const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(false).then(resolve).catch(reject);
    return;
  }
  const timer = setTimeout(() => reject(new Error('Auth timeout')), 10000);
  const unsub = onAuthStateChanged(auth, (user) => {
    unsub();
    clearTimeout(timer);
    if (user) user.getIdToken(false).then(resolve).catch(reject);
    else reject(new Error('Not authenticated'));
  });
});

const authHeaders = async () => ({ Authorization: `Bearer ${await getFirebaseToken()}` });

export const listDocAiSummaries = async (transactionId) => {
  const res = await apiClient.get(base(transactionId), { headers: await authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
};

// sourceKey: { sourceKind, sourceRef, displayName?, envelopeDocumentId?, sourceState? }
export const generateDocAiSummary = async (transactionId, sourceKey) => {
  const body = { sourceKind: sourceKey.sourceKind, sourceRef: sourceKey.sourceRef };
  if (sourceKey.displayName) body.displayName = sourceKey.displayName;
  if (sourceKey.envelopeDocumentId) body.envelopeDocumentId = String(sourceKey.envelopeDocumentId);
  if (sourceKey.sourceState) body.sourceState = sourceKey.sourceState;
  const res = await apiClient.post(base(transactionId), body, { headers: await authHeaders() });
  return res.data;
};

export const getDocAiSummary = async (transactionId, summaryId, { version, checkStale = true } = {}) => {
  const params = { checkStale };
  if (version) params.version = version;
  const res = await apiClient.get(`${base(transactionId)}/${summaryId}`, { headers: await authHeaders(), params });
  return res.data;
};

export const updateDocAiSummaryItem = async (transactionId, summaryId, itemId, body) => {
  const res = await apiClient.patch(`${base(transactionId)}/${summaryId}/items/${itemId}`, body, {
    headers: await authHeaders(),
  });
  return res.data;
};

// Prefer the backend's user-facing message when there is one.
export const errorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 429) return message || 'Summaries are busy right now. Please try again in a minute.';
  if (status === 403) return 'You don\'t have access to this document\'s summary.';
  if (message && typeof message === 'string') return message;
  return fallback;
};
